import Head from 'next/head';
import type { HTMLAttributes } from 'react';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import kebabCase from 'lodash/kebabCase';
import mapKeys from 'lodash/mapKeys';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import { Locale } from '@fixter/i18n';
import { Row, Col } from '$src-components/atoms/Grid';
import { ReviewProvider } from '$constants/review-providers';
import type { CmsPageCollectionItems } from '$cms/types';
import type { ContentComponent } from '$cms/ContentComponent';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { vars } from '$util/theme';
import { PreloadImage } from '$util/PreloadImage';
import { TrustPilot } from '$quantum/atoms/TrustPilot';
import { TRUSTPILOT_TEMPLATE_ID } from '$constants/trustpilot';
import { GoogleReviewsSummary } from '$quantum/molecules/GoogleReviewsSummary';
import { PartOfRenault } from '$quantum/molecules/PartOfRenault';
import { SpeechBubble } from '$src-components/atoms/SpeechBubble';
import { getCloudinarySrc, cloudinaryLoader } from '$src-components/atoms/Img/cloudinary';
import { Img } from '$src-components/atoms/Img';
import { VrmLookupModal } from '$quantum/molecules/VrmLookupModal';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import { replaceCollectionPlaceholders } from '$cms/replaceCollectionPlaceholders';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { useResizeViewport } from '$util/hooks/useResizeViewport';
import { useActiveExperiment } from '$util/abTest';
import { ActiveExperiments } from '$util/abTest/experiments';
import { VrmLookupV2 } from '$quantum/molecules/VrmLookupV2';
import { useTrustpilotHeight } from '$quantum/atoms/TrustPilot/useTrustPilotHeight';
import { Hero24 } from '@/components/organisms/Hero24';
import type { MakeContentfulRichTextRendererOptions } from '@/cms/richTextContentHelpers';
import styles from './index.module.scss';
import { renderRichTextHeading } from './helpers';
import {
  HeroSectionSchema,
  type HeroSectionConfiguration,
  type HeroSectionProps,
  type HeroSectionTranslatedEntities,
  defaultConfiguration,
} from './schema';

const renderReviewsSummary = (
  translatedEntities: HeroSectionTranslatedEntities,
  trustpilotHeight: string,
  reviewsProvider?: ReviewProvider
) => {
  const {
    googleReviewsStars,
    googleReviewsTotalReviews,
    googleReviewsLink,
    googleReviewsLogo,
    googleReviewsReviewsText,
  } = translatedEntities || {};
  const displayGoogleStars = googleReviewsStars && googleReviewsTotalReviews && googleReviewsLink;
  return reviewsProvider === ReviewProvider.TRUSTPILOT ? (
    <TrustPilot templateId={TRUSTPILOT_TEMPLATE_ID} height={trustpilotHeight} width="100%" theme="dark" />
  ) : (
    displayGoogleStars && (
      <GoogleReviewsSummary
        className={styles.hero__form__reviewdesktop__googlereview}
        rating={googleReviewsStars.number}
        link={googleReviewsLink.value}
        reviewsText={googleReviewsReviewsText?.value}
        providerLogo={
          googleReviewsLogo || {
            key: 'google-logo',
            url: 'https://res.cloudinary.com/fxtr/image/upload/v1688549036/landing/logos/google_review_fv9hui.svg',
            width: 36,
            height: 31,
            alt: 'Google Logo',
          }
        }
        providerLogoWidth={googleReviewsLogo?.width || 36}
        providerLogoHeight={googleReviewsLogo?.height || 31}
        shortVersion
        darkTheme
      />
    )
  );
};

function DoubleLinesImg() {
  return (
    <>
      <PreloadImage
        provider="cloudinary"
        src="/hero-landing/double_line_hero_rq4wl1.png"
        displayFrom="large"
        width={800}
      />
      <Img
        className={styles.hero__lines}
        provider="cloudinary"
        src="/hero-landing/double_line_hero_rq4wl1.png"
        alt="Double line"
        width={800}
        height={800}
        loading="eager"
        priority
      />
    </>
  );
}

const heroImageCloudinaryLoader = cloudinaryLoader();
const heroImageSrcs = {
  desktop: heroImageCloudinaryLoader({
    src: getCloudinarySrc('/hero-landing/fixie_hero_desktop_gmimiq.webp'),
    width: 580,
  }),
  desktop2x: heroImageCloudinaryLoader({
    src: getCloudinarySrc('/hero-landing/fixie_hero_desktop_gmimiq.webp'),
    width: 1160,
  }),
  desktop3x: heroImageCloudinaryLoader({
    src: getCloudinarySrc('/hero-landing/fixie_hero_desktop_gmimiq.webp'),
    width: 1740,
  }),
  mobile: heroImageCloudinaryLoader({
    src: getCloudinarySrc('/hero-landing/fixie_hero_mobile_hpyuh4.webp'),
    width: 240,
  }),
  mobile2x: heroImageCloudinaryLoader({
    src: getCloudinarySrc('/hero-landing/fixie_hero_mobile_hpyuh4.webp'),
    width: 480,
  }),
  mobile3x: heroImageCloudinaryLoader({
    src: getCloudinarySrc('/hero-landing/fixie_hero_mobile_hpyuh4.webp'),
    width: 720,
  }),
};

function MascotImg({ className }: HTMLAttributes<HTMLPictureElement>) {
  return (
    <picture className={className}>
      <Head>
        <link
          rel="preload"
          as="image"
          imageSrcSet={`${heroImageSrcs.mobile3x} 3x, ${heroImageSrcs.mobile2x} 2x, ${heroImageSrcs.mobile}`}
          sizes={`(max-width: ${vars.mediaQueryBreakpoints.small - 1}px) 125w, 125w`}
          media={`(max-width: ${vars.mediaQueryBreakpoints.small - 1}px)`}
        />
        <link
          rel="preload"
          as="image"
          imageSrcSet={`${heroImageSrcs.mobile3x} 3x, ${heroImageSrcs.mobile2x} 2x, ${heroImageSrcs.mobile}`}
          sizes={`(min-width: ${vars.mediaQueryBreakpoints.small}px) and (max-width: ${vars.mediaQueryBreakpoints.medium}px) 150w, 150w`}
          media={`(min-width: ${vars.mediaQueryBreakpoints.small}px) and (max-width: ${vars.mediaQueryBreakpoints.medium}px)`}
        />
        <link
          rel="preload"
          as="image"
          imageSrcSet={`${heroImageSrcs.mobile3x} 3x, ${heroImageSrcs.mobile2x} 2x, ${heroImageSrcs.mobile}`}
          sizes={`(min-width: ${vars.mediaQueryBreakpoints.medium}px) and (max-width: ${vars.mediaQueryBreakpoints.large}px) 200w, 200w`}
          media={`(min-width: ${vars.mediaQueryBreakpoints.medium}px) and (max-width: ${vars.mediaQueryBreakpoints.large}px)`}
        />
        <link
          rel="preload"
          as="image"
          imageSrcSet={`${heroImageSrcs.desktop3x} 3x, ${heroImageSrcs.desktop2x} 2x, ${heroImageSrcs.desktop}`}
          sizes={`(min-width: ${vars.mediaQueryBreakpoints.large}px) 450w, 450w`}
          media={`(min-width: ${vars.mediaQueryBreakpoints.large}px)`}
        />
      </Head>
      <source
        srcSet={`${heroImageSrcs.mobile3x} 3x, ${heroImageSrcs.mobile2x} 2x, ${heroImageSrcs.mobile}`}
        media={`(max-width: ${vars.mediaQueryBreakpoints.small - 1}px)`}
        width={125}
        height={250}
      />
      <source
        srcSet={`${heroImageSrcs.mobile3x} 3x, ${heroImageSrcs.mobile2x} 2x, ${heroImageSrcs.mobile}`}
        media={`(min-width: ${vars.mediaQueryBreakpoints.small}px) and (max-width: ${
          vars.mediaQueryBreakpoints.medium - 1
        }px)`}
        width={150}
        height={300}
      />
      <source
        srcSet={`${heroImageSrcs.mobile3x} 3x, ${heroImageSrcs.mobile2x} 2x, ${heroImageSrcs.mobile}`}
        media={`(min-width: ${vars.mediaQueryBreakpoints.medium}px) and (max-width: ${
          vars.mediaQueryBreakpoints.large - 1
        }px)`}
        width={200}
        height={385}
      />
      <source
        srcSet={`${heroImageSrcs.desktop3x} 3x, ${heroImageSrcs.desktop2x} 2x, ${heroImageSrcs.desktop}`}
        media={`(min-width: ${vars.mediaQueryBreakpoints.large}px)`}
        width={450}
        height={473}
      />
      <img
        className={styles.hero__fixie_asset}
        src={heroImageSrcs.mobile}
        alt="Fixie"
        width={125}
        height={250}
        loading="eager"
      />
    </picture>
  );
}

function HeroIllustrations({ translatedEntities }: { translatedEntities: HeroSectionTranslatedEntities }) {
  const { speechBubbleOne, speechBubbleTwo, speechBubbleThree } = translatedEntities || {};

  return (
    <div className={styles.hero__illustrations}>
      <div className={styles.hero__illustrations__speechbubbles}>
        <SpeechBubble
          type="large"
          className={classNames(
            styles.hero__illustrations__speechbubble,
            styles.hero__illustrations__speechbubble__one,
            styles.FixedFirstBubble
          )}
        >
          <span>{speechBubbleOne.value}</span>
        </SpeechBubble>
        <SpeechBubble
          type="large"
          className={classNames(
            styles.hero__illustrations__speechbubble,
            styles.hero__illustrations__speechbubble__two,
            styles.FixedSecondBubble
          )}
        >
          <span>{speechBubbleTwo?.value}</span>
        </SpeechBubble>
        <SpeechBubble
          type="large"
          className={classNames(
            styles.hero__illustrations__speechbubble,
            styles.hero__illustrations__speechbubble__three,
            styles.FixedThirdBubble,
            styles.FixedThirdBubble
          )}
        >
          <span>{speechBubbleThree?.value}</span>
        </SpeechBubble>
      </div>

      <MascotImg className={styles.hero__fixie} />
    </div>
  );
}

const makePlaceholders = (
  collectionItems: CmsPageCollectionItems
): MakeContentfulRichTextRendererOptions['placeholder'] =>
  mapValues(
    mapKeys(collectionItems, (value, key) => {
      return kebabCase(key);
    }),
    (value) => {
      return value?.name || '';
    }
  );

export const HeroSection: ContentComponent<
  HeroSectionTranslatedEntities,
  HeroSectionConfiguration,
  HeroSectionProps
> = function HeroSection({
  translatedEntities,
  configuration,
  extraParams,
  targetBlank,
  formAction = '/vehicles/add',
  className,
}): JSX.Element {
  const {
    campaign,
    location: { pathname },
    locale,
  } = useAppContext<AppContextGlobal>();
  const cmsPageContext = useCmsPageContext();
  const { reviewsProvider, sku } = merge(defaultConfiguration, configuration);
  const trustpilotHeight = useTrustpilotHeight();

  const [showStickyButton, setShowStickyButton] = useState(false);

  const [stickyButtonIntersectionObserver, setStickyButtonIntersectionObserver] = useState<
    IntersectionObserver | undefined
  >(undefined);

  const collectionItems = cmsPageContext?.pageRoutes?.[0]?.collectionItems;

  const {
    heading,
    newHeroHeading,
    subheading,
    mainCta,
    postcodeLabel,
    vrmLabel,
    invalidPostcodeMessage,
    invalidVRMMessage,
    missingPostcodeMessage,
    missingVRMMessage,
    googleReviewsBasedOnText,
    googleReviewsLink,
    googleReviewsLogo,
    googleReviewsReviewsText,
    googleReviewsStars,
    googleReviewsTotalReviews,
    price,
  } = translatedEntities || {};

  const title = useMemo(() => {
    if ('content' in heading) {
      if (collectionItems) {
        const placeholders = makePlaceholders(collectionItems);

        const richTextContent = renderRichTextHeading(heading.content, {
          ...placeholders,
        });
        return richTextContent;
      }
      return renderRichTextHeading(heading.content);
    }
    return collectionItems ? replaceCollectionPlaceholders(heading.value, collectionItems) : heading.value;
  }, [collectionItems, heading]);

  const subTitle = useMemo(() => {
    if (!subheading) return undefined;
    return collectionItems
      ? replaceCollectionPlaceholders(subheading.value, collectionItems)
      : subheading.value;
  }, [collectionItems, subheading]);

  const headingElement = useRef<HTMLHeadingElement>(null);
  const heroVrmElement = useRef<HTMLDivElement>(null);

  /**
   * @NOTE implementation currently fires the intersection observer on intersection
   * for all viewports because
   *
   * 1. we have not yet moved the useResizeViewport hook
   * to the new grid breakpoints
   * 2. This needs to be run in the useEffect hook so not sure of the best way to
   * do it in both hooks at the moment
   *
   * This means the IntersectionObserver callback will be run for every breakpoint when
   * the intersection ratio threshold is crossed - this is still much better performance
   * than using a scroll listener and with greatly reduce jank already.
   *
   * @TODO extract the Intersection Observers callback function outside the component scope
   *
   * @TODO when the useResizeViewport component is using the new breakpoints,
   * then refactor so that the observer is initialised independtly, that the target is observed
   * when the viewport is matched and unobserved when it does not.
   */
  useEffect(() => {
    if (campaign || !headingElement.current) return undefined;

    const contentSectionEl = document.querySelector<HTMLElement>('.ContentSectionContainer');
    const topNavBarEl = document.querySelector<HTMLElement>('.TopNavigationBar');

    if (!contentSectionEl || !topNavBarEl) return undefined;

    const isTop =
      (className || '').split(' ').includes('top') &&
      contentSectionEl.previousElementSibling === topNavBarEl.parentElement;

    if (!isTop) {
      contentSectionEl.firstElementChild?.classList.remove('top');
      return undefined;
    }

    topNavBarEl.classList.add('transparent');
    topNavBarEl.classList.add('transition');

    const observerTarget = headingElement.current;
    const toggleNavBg = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        if (window.innerWidth >= 600) {
          if (entry.intersectionRatio < 1) {
            // make nav opaque
            topNavBarEl.classList.remove('transparent');
          } else {
            // make nav transparent
            topNavBarEl.classList.add('transparent');
          }
        }
      });
    };
    const observer = new IntersectionObserver(toggleNavBg, {
      rootMargin: '-80px 0px 0px',
      threshold: [1],
    });

    observer.observe(observerTarget);

    return () => {
      observer.unobserve(observerTarget);
    };
  }, [headingElement, campaign, className]);

  useEffect(() => {
    if (heroVrmElement.current) {
      const target = heroVrmElement.current;
      const observer = new IntersectionObserver(
        ([observedTarget]) => {
          const { isIntersecting } = observedTarget;
          setShowStickyButton(!isIntersecting);
        },
        {
          threshold: [0],
        }
      );

      observer.observe(target);

      setStickyButtonIntersectionObserver(observer);

      return () => {
        observer.unobserve(target);
      };
    }
    return () => undefined; // needed due to consistent return lint rule
  }, [heroVrmElement, setStickyButtonIntersectionObserver, setShowStickyButton]);

  useResizeViewport({
    breakpoint: 'small',
    direction: 'up',
    fn: (isDesktop) => {
      // @NOTE if desktop, don't show
      if (heroVrmElement?.current) {
        if (isDesktop) {
          stickyButtonIntersectionObserver?.unobserve(heroVrmElement.current);
          setShowStickyButton(false);
        } else {
          stickyButtonIntersectionObserver?.observe(heroVrmElement.current);
        }
      }
    },
  });

  const HeroSectionRemoveInputsFR = useActiveExperiment(ActiveExperiments.HeroSectionRemoveInputsFR, 'B');

  const HeroSectionRemoveInputs = (locale === Locale.EN_GB || HeroSectionRemoveInputsFR) && pathname === '/';

  const RevisionAndVidange = useActiveExperiment(ActiveExperiments.RevisionAndVidangeLandingPageFR, 'B');
  const CarServiceNewLandingPage = useActiveExperiment(ActiveExperiments.NewHeroAndUSPsLandingPageUK, 'B');

  return RevisionAndVidange || CarServiceNewLandingPage ? (
    <>
      <Hero24
        className={classNames(styles.topAdjustment, className)}
        vrmRef={heroVrmElement}
        headingRef={headingElement}
        heading={newHeroHeading || heading}
        postcodeLabel={postcodeLabel}
        invalidPostcodeMessage={invalidPostcodeMessage}
        missingPostcodeMessage={missingPostcodeMessage}
        vrmLabel={vrmLabel}
        invalidVRMMessage={invalidVRMMessage}
        missingVRMMessage={missingVRMMessage}
        mainCta={mainCta}
        googleReviewsBasedOnText={googleReviewsBasedOnText}
        googleReviewsLink={googleReviewsLink}
        googleReviewsLogo={googleReviewsLogo}
        googleReviewsReviewsText={googleReviewsReviewsText}
        googleReviewsStars={googleReviewsStars}
        googleReviewsTotalReviews={googleReviewsTotalReviews}
        price={price}
        locale={locale}
      />
      {showStickyButton && (
        <div className={classNames(`${HeroSection.displayName}StickyCta`, styles.stickyCTA)}>
          <VrmLookupModal
            fullWidth
            id="VrmLookupModalButton"
            toggleButton={{ size: 'L', children: mainCta?.value || 'Get an instant quote' }}
          />
        </div>
      )}
    </>
  ) : (
    <Row
      className={classNames(
        HeroSection.displayName,
        styles.row,
        styles.topAdjustment,
        className,
        HeroSectionRemoveInputs ? styles.row__modalVrmLookup : ''
      )}
    >
      <Col extraSmall={4} small={3} medium={5} large={7} className={styles.col}>
        <div ref={headingElement}>
          <HeadingLevels semanticLevel={1} styleLevel={1} className={styles.hero__title}>
            {title}
          </HeadingLevels>
          {subTitle && <p className={styles.hero__subheading}>{subTitle}</p>}
        </div>
      </Col>
      <Col
        extraSmall={4}
        small={3}
        smallStart={4}
        medium={5}
        mediumStart={8}
        className={classNames(styles.col, styles.col__images)}
      >
        <DoubleLinesImg />
        <HeroIllustrations translatedEntities={translatedEntities} />
      </Col>
      <Col extraSmall={4} small={3} medium={5}>
        <div ref={heroVrmElement} className={HeroSectionRemoveInputs ? styles.col__ctaLookupModal : ''}>
          {!HeroSectionRemoveInputs ? (
            <VrmLookupV2
              sku={sku}
              targetBlank={targetBlank}
              formAction={formAction}
              extraParams={extraParams}
              buttonText={mainCta?.value}
            />
          ) : (
            <VrmLookupModal
              id="HomepageHeroSectionCta"
              toggleButton={{ size: 'L', children: mainCta?.value, fullWidth: true }}
            />
          )}
        </div>
      </Col>
      <Col extraSmall={4} small={3} medium={6} className={styles.hero__review}>
        {renderReviewsSummary(translatedEntities, trustpilotHeight, reviewsProvider)}
      </Col>
      <Col
        extraSmall={2}
        extraSmallStart={2}
        small={2}
        smallStart={5}
        medium={4}
        mediumStart={8}
        className={styles.hero__renault}
      >
        <PartOfRenault layout="horizontal" preload />
      </Col>
      {showStickyButton && (
        <div className={styles.stickyCTA}>
          <VrmLookupModal
            fullWidth
            id="VrmLookupModalButton"
            toggleButton={{ size: 'L', children: mainCta?.value || 'Get an instant quote' }}
          />
        </div>
      )}
    </Row>
  );
};

HeroSection.displayName = 'HeroSection';
HeroSection.schema = HeroSectionSchema;
